import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChatService } from '../services/chat/chat.services';

export interface AgentMetadata {
  topic?: string;
  context?: string;
  question?: string;
  options?: any;
  correct_option?: any;
  [key: string]: any;
}

export const useAgent = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const chatService = new ChatService();
  const navigate = useNavigate();

  const talk = async (params: {
    message: string;
    step_type: string;
    metadata?: AgentMetadata;
    is_clue_requested?: boolean;
    is_explanation_requested?: boolean;
    history?: { role: 'user' | 'assistant', content: string }[];
    lesson_id?: number;
    level_id?: number;
  }) => {
    setIsLoading(true);
    try {
      // 1. Get user message
      const userMessage = params.message;

      // 2. Get history (either provided or default to empty)
      const history = params.history || [];

      // Map parameters to the new V2 context structure
      let mode: 'help' | 'clue' | 'explanation' = 'help';
      if (params.is_clue_requested) mode = 'clue';
      if (params.is_explanation_requested) mode = 'explanation';

      const contextPayload = {
        current_page: 'learning',
        step_type: params.step_type,
        topic: params.metadata?.topic,
        lesson_id: params.lesson_id,
        level_id: params.level_id,
        metadata: {
          ...params.metadata,
          mode: mode
        }
      };

      // 3. Call API V2
      const response = await chatService.TALKWITHAGENT_V2({
        message: userMessage || (params.is_clue_requested ? t('agentRequestClue') : t('agentRequestExplanation')),
        context: contextPayload,
        history: history,
      });

      if (response) {
        const { message: responseMessage, client_action } = response;

        // 5. Intercept and execute client action frames (e.g. navigation)
        if (client_action && client_action.type === 'navigate' && client_action.route) {
          setTimeout(() => {
            navigate(client_action.route);
          }, 1500); // 1.5s delay to allow student to read the Hebrew redirect message
        }

        return response;
      }

      return null;
    } catch (error) {
      console.error('Agent V2 interaction failed:', error);
      return { message: 'COMMUNICATION_ERROR', isError: true };
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = (message: string, step_type: string, metadata?: AgentMetadata, history?: { role: 'user' | 'assistant', content: string }[], lesson_id?: number, level_id?: number) => {
    return talk({ message, step_type, metadata, history, lesson_id, level_id });
  };

  const getClue = (step_type: string, metadata?: AgentMetadata, history?: { role: 'user' | 'assistant', content: string }[], lesson_id?: number, level_id?: number) => {
    return talk({ message: '', step_type, metadata, is_clue_requested: true, history, lesson_id, level_id });
  };

  const getExplanation = (step_type: string, metadata?: AgentMetadata, history?: { role: 'user' | 'assistant', content: string }[], lesson_id?: number, level_id?: number) => {
    return talk({ message: '', step_type, metadata, is_explanation_requested: true, history, lesson_id, level_id });
  };

  return {
    isLoading,
    sendMessage,
    getClue,
    getExplanation,
  };
};
